<template>
  <div>
    <portal to="search">
      <div class="-ml-4 flex">
        <inertia-link :href="route('dispatches.delivery')"
                      class="text-blue-500 hover:underline p-4"
        >
        <Icon name="arrow-left" class="h-5 w-5" />
        </inertia-link>
      </div>
    </portal>
    <div class="flex gap-x-3 text-xl mb-8">
      <inertia-link :href="route('dispatches.delivery')"
                    class="text-blue-500 hover:underline"
      >
        Delivery List
      </inertia-link> /
      <h1 class="">Dispatch Schedule - {{ isDeliveryAddress ? 'Local' : 'Boat' }} Deliveries</h1>
    </div>

    <div class="flex justify-between flex-col md:flex-row items-center">
      <div class="space-x-4 py-2">
        <label v-for="option in groupByOptions" :key="'radio-group-'+option.value">
            <input v-model="filter.group_by" type="radio" name="group" :value="option.value" />
            {{ option.label }}
        </label>
      </div>
      <div class="space-x-4 py-2">
        <inertia-link :href="route('delivery-notes.schedules', {type: locationType === 'address' ? 'vessel_location' : 'address'})" class="text-blue-500 hover:text-blue-500 font-bold">
          &rightarrow; Show Deliveries to {{ locationType === 'address' ? 'Vessels' : 'Addresses'}}
        </inertia-link>
      </div>
    </div>

    <div class="flow-root">
      <div class="overflow-x-auto">
        <div class="inline-block min-w-full p-4 align-middle bg-white border">
          <table class="w-full">
            <thead class="text-sm">
            <tr class="border bg-white">
              <th class="py-2 border w-1/12">
                <span v-if="!isDeliveryAddress">Vessel</span>
                Location
              </th>
              <th class="py-2 border w-1/12" v-if="!isDeliveryAddress">
                <span v-if="groupBy === 'boat'">Delivery Date</span><span v-else>Boat</span>
              </th>
              <th class="py-2 border w-1/12">Customer</th>
              <th class="py-2 border w-1/12">Order</th>
              <th class="py-2 border w-1/12">TQTY</th>
              <th class="py-2 border w-1/12">Delivery Status</th>
              <th class="py-2 border w-1/12">Order Status</th>
              <th class="py-2 border w-1/12">DN Status</th>
              <th class="py-2 border w-1/12">Team</th>
            </tr>
            <tr class="border bg-white">
                <th v-if="!isDeliveryAddress">
                  <SelectInput v-model="filter.vessel_location">
                    <option :value="null">All</option>
                    <option :value="location" v-for="location in vesselLocations" :key="location">{{ location }}</option>
                  </SelectInput>
                </th>
                <th v-else>
                  <SelectInput v-model="filter.delivery_location">
                    <option :value="null">All</option>
                    <option
                      v-for="(location, locKey) in locationList"
                      :key="'location-'+locKey"
                      :value="locKey"
                    >
                      {{ location }}
                    </option>
                  </SelectInput>
                </th>

              <th v-if="!isDeliveryAddress" class="border w-1/12">
                <SelectInput v-if="groupBy === 'date'" v-model="filter.boat_id">
                  <option :value="null">All</option>
                  <option :value="boat.id" v-for="boat in boatList" :key="boat.id">{{ boat.name }}</option>
                </SelectInput>
                <FormInput v-else v-model="filter.delivery_date" type="date"/>
              </th>
              <th class="border w-1/12 bg-gray-100"></th>
              <th class="border w-1/12">
                <FormInput v-model="filter.order_num" placeholder="search order number" />
              </th>
              <th class="border w-1/12 bg-gray-100"></th>
              <th class="border w-1/12">
                <SelectInput v-model="filter.delivery_status">
                  <option :value="null">All</option>
                  <option value="not_started">Not Started</option>
                  <option
                    v-for="(option, optionKey) in deliveryStatusList"
                    :value="optionKey"
                    :key="optionKey"
                  >{{ option }}</option>
                </SelectInput>
              </th>
              <th class="border w-1/12">
                <SelectInput v-model="filter.status">
                  <option :value="null">All</option>
                  <option :value="status" v-for="status in statusList" :key="status">{{ status }}</option>
                </SelectInput>
              </th>
              <th class="border w-1/12">
                <SelectInput v-model="filter.note_status">
                  <option :value="null">All</option>
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                </SelectInput>
              </th>
              <th class="border w-1/12">
                <SelectInput v-model="filter.delivery_team">
                  <option :value="null">All</option>
                  <option :value="team.id" v-for="team in deliveryTeams" :key="team.id">{{ team.name }}</option>
                </SelectInput>
              </th>
            </tr>
            </thead>
            <tbody class="text-sm">
            <template v-for="(data, groupByFieldValue) in schedule">
              <template v-if="!isDeliveryAddress">
              <tr class="border bg-gray-800 text-white">
                  <td v-if="groupBy === 'date'" colspan="9" :class="{'px-4 py-2' : true, 'bg-green-200 text-green-700': dateIsToday(groupByFieldValue), 'bg-red-100 text-red-800': dateIsPast(groupByFieldValue)}">
                    <div class="flex w-full">
                      <div class="flex-1">{{  groupByFieldValue | localDateWithWeekDay }}</div>
                      <div class="flex-1">{{ calculateSum(groupByFieldValue) }}</div>
                    </div>
                  </td>
                  <td v-else colspan="9" class="px-4 py-2 capitalize">
                      {{ getBoatName(groupByFieldValue) }}
                  </td>
              </tr>
              </template>
              <template v-else>
                <tr v-if="groupBy === 'address'"  class="border bg-gray-700 text-white">
                    <td colspan="8" class="px-4 py-2 capitalize">
                      {{ locationList[groupByFieldValue] }}
                    </td>
                </tr>
                <tr v-else  :class="{'border' : true, 'bg-green-200 text-green-700': dateIsToday(groupByFieldValue), 'bg-red-100 text-red-800': dateIsPast(groupByFieldValue), 'bg-blue-50 text-blue-600': dateIsFuture(groupByFieldValue)}">
                  <td  colspan="8" class="px-4 py-2">{{ groupByFieldValue | localDateWithWeekDay }}</td>
                </tr>
              </template>
              <template v-for="(rows, subGroupValue) in data">
                <tr v-for="(row, ridx) in rows" :key="subGroupValue+'-'+row.id+'-'+ridx" class="border" >
                  <td class="pl-8 align-text-top py-1">
                    <template v-if="!isDeliveryAddress">
                      {{ row.vessel_location }}
                    </template>
                  </td>
                  <td v-if="!isDeliveryAddress && ridx === 0" :rowspan="getRowCount(rows)" class="pl-8 align-text-top py-1 border-l border-r">
                    <span v-if="groupBy === 'date'">{{ row.boat_name }}</span>
                    <span v-else :class="{'text-green-700': dateIsToday(row.delivery_date), 'text-red-800': dateIsPast(row.delivery_date)}">
                      {{ row.delivery_date | localDateWithWeekDay }}
                    </span>
                  </td>
                  <td class="text-left px-3">{{ row.customer_account_name }}</td>
                  <td class="text-center py-2"><inertia-link :href="route('customer.orders.show',row.id)">{{ row.id }}</inertia-link></td>
                  <td class="text-center"><inertia-link :href="route('customer.orders.show',row.id)">{{ row.items_total_qty }}</inertia-link></td>
                  <td class="text-center">
                    <select v-if="row.delivery_team" v-model="row.delivery_status" @input="(evt) => updateDeliveryStatus(evt.target.value, row)">
                      <option :disabled="row.delivery_status === null" :value="null" :selected="row.delivery_status === '' || row.delivery_status === null" >Not Started</option>
                      <option
                        v-for="(option, optionKey) in deliveryStatusList"
                        :value="optionKey"
                        :key="'row-'+row.id+'-status-'+optionKey"
                        :disabled="row.delivery_status === optionKey"
                        :selected="row.delivery_status === optionKey"
                      >{{ option }}</option>
                    </select>
                    <AssignTeamModal
                      v-if="!row.delivery_team && row.status === 'invoiced' || row.status === 'delivery_in_progress' && row.note_status === 'pending'"
                      v-slot="{open}"
                      :key="'row-'+row.id"
                      :note="{
                        id: row.note_id,
                        delivery_date: row.delivery_date
                      }"
                    >
                      <button class="btn-primary px-1.5 py-1 text-xs" @click="open" type="button">Assign Team</button>
                    </AssignTeamModal>
                    <a :href="printOrderUrl(row)"
                       v-if="row.delivery_status === 'started'"
                       target="_blank"
                       class="ml-3 rounded bg-gray-300 text-xs px-1 py-1"
                    >Print</a>
                  </td>
                  <td class="text-center capitalize">{{ row.status }}</td>
                  <td class="text-center capitalize"><Badge :variant="row.note_status === 'pending' ? 'gray' : row.note_status === 'completed' ? 'green' : 'blue'">{{ row.note_status }}</Badge></td>
                  <td class="text-center">
                    <div v-if="row.delivery_team" class="flex justify-between pr-1">
                      <div>{{ row.delivery_team}}</div>
                      <AssignTeamModal
                        v-if="!row.delivery_status"
                        v-slot="{open}"
                        :key="'row-'+row.id"
                        :note="{
                        id: row.note_id,
                        delivery_date: row.delivery_date
                      }"
                      >
                        <button class="btn-primary px-2 py-1.5 text-xs" @click="open" type="button">A</button>
                      </AssignTeamModal>
                    </div>
                    <span v-else class="text-red-700">NOT ASSIGNED</span>
                  </td>
                </tr>
              </template>
            </template>
            </tbody>
          </table>
        </div>
      </div>
        </div>
  </div>
</template>

<script>
import Layout from "@/Shared/Admin/Layout.vue";
import debounce from "lodash/debounce";
import pickBy from "lodash/pickBy";
import SelectInput from "../../../Shared/SelectInput.vue";
import moment from "moment-timezone";
import AssignTeamModal from "./AssignTeamModal.vue";
import FormInput from "../../../Shared/Form/FormInput.vue";
import {sumBy} from "lodash";
import Icon from "../../../Shared/Icon.vue";
import Badge from "../../../Shared/Badge.vue";

export default {
  name: 'DeliverySchedule',
  components: {Badge, Icon, FormInput, AssignTeamModal, SelectInput},
  layout: Layout,
  metaInfo: {
    title: 'Delivery Schedules',
  },
  props: {
    groupBy: String,
    groupByOptions: Array,
    schedule: [Object, Array],
    deliveryTeams: Array,
    boatList: Array,
    statusList: Array,
    deliveryStatusList: Object,
    vesselLocations: Array,
    locationType: String,
    locationList: [Object, Array],
    filters: Object,
  },

  data() {
    return {
      assignTeam: {
        show: false,
        team_id: null,
        order: null,
      },
      filter: {
        group_by: this.groupBy,
        delivery_date: this.filters.delivery_date || null,
        delivery_location: this.filters.delivery_location || null,
        vessel_location: this.filters.vessel_location || null,
        boat_id: this.filters.boat_id || null,
        order_num: this.filters.order_num || null,
        status: this.filters.status || null,
        delivery_team: this.filters.delivery_team || null,
        note_status: this.filters.note_status || null,
        delivery_status: this.filters.delivery_status || null
      },

      statusUpdateForm: this.$inertia.form({
        delivery_status: null,
      })
    }
  },

  computed: {
    isDeliveryAddress() {
      return this.locationType === 'address'
    }
  },
  watch: {
    filter : {
      handler: debounce(function() {
        let query = pickBy(this.filter)
        query.type = this.locationType;
        this.$inertia.visit(this.route('delivery-notes.schedules', Object.keys(query).length ? query : { remember: 'forget'}), {}, { preserveState: true })
      }, 300),
      deep: true
    }
  },

  methods: {
    getRowCount(rows) {
      return rows.length;
    },

    getBoatName(boat_id) {
      let BoatFound = this.boatList.find((boat) => {
        // console.log('checking boat.id', boat.id);
        // console.log('for boat_id', boat_id);
        return boat.id === parseInt(boat_id)
      })
      if(BoatFound) {
        return BoatFound.name
      }

      return '[ERROR NO NAME]'
    },

    dateIsToday(deliveryDate) {
      let currentDate = moment();
      return currentDate.isSame(deliveryDate, 'day');
    },
    dateIsPast(deliveryDate) {
      let currentDate = moment();
      return currentDate.isAfter(deliveryDate, 'day');
    },
    dateIsFuture(deliveryDate) {
      let currentDate = moment();
      return currentDate.isBefore(deliveryDate, 'day');
    },

    printOrderUrl(note) {
      return this.route('delivery-notes.print-schedule', {
        note: note.note_id,
        order: note.id,
      })
    },

    updateDeliveryStatus(deliveryStats, item) {
      this.statusUpdateForm.transform((data) => ({
        ...data,
        delivery_status: deliveryStats
      })).post(this.route('delivery-notes.delivery-status.update', {
        note: item.note_id,
        order: item.id,
      }))

    },

    calculateSum(date) {
      let dateTotal = 0;

      if (this.schedule[date]) {
        const deliveries = Object.values(this.schedule[date]).flat();
        dateTotal = sumBy(deliveries, 'items_total_qty');
      }

      return dateTotal;
    }
  }
}
</script>
